import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { IconButton, Modal, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

import { SingleHighlight } from '..';

const MODAL_MAX_WIDTH_PX = 1100;
const MODAL_WIDTH_WV = 80;
const MODAL_TOP_MARGIN_PX = 64;
const MODAL_HEIGHT_CALC = 'calc(100vh - 120px)';
const MODAL_MAX_HEIGHT_PX = 660;
const NAV_BUTTON_WIDTH_PX = 40;
const NAV_BUTTON_SPACING_PX = 16;
const NAV_BUTTON_COMBINED_WIDTH_PX = NAV_BUTTON_WIDTH_PX + NAV_BUTTON_SPACING_PX;

// This wrapper is needed so that the buttons in the NavigationButtonContainer can be visible,
// even though the parent StyledModal uses overflow: hidden;
// which can't be removed due to other styling needs
// More on this:
// https://medium.com/@thomas.sameshima/css-overriding-the-parents-overflow-hidden-90c75a0e7296
const Wrapper = styled('div')`
  transform: scale(0);
`;

const StyledModal = styled(Modal)`
  height: ${MODAL_HEIGHT_CALC};
  max-height: ${MODAL_MAX_HEIGHT_PX}px;
  overflow: hidden;

  max-width: ${MODAL_MAX_WIDTH_PX}px;
  width: ${MODAL_WIDTH_WV}vw;
`;

const NavigationButtonContainer = styled('div')`
  height: ${MODAL_HEIGHT_CALC};
  max-height: ${MODAL_MAX_HEIGHT_PX}px;
  width: calc(${MODAL_WIDTH_WV}vw + (2 * ${NAV_BUTTON_COMBINED_WIDTH_PX}px));
  max-width: ${MODAL_MAX_WIDTH_PX + 2 * NAV_BUTTON_COMBINED_WIDTH_PX}px;
  min-width: ${320 + 2 * NAV_BUTTON_COMBINED_WIDTH_PX}px;
  transform: translateX(-${NAV_BUTTON_COMBINED_WIDTH_PX}px);
  position: fixed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
`;

const NavigationIconButton = styled(IconButton)`
  filter: invert(1);
  pointer-events: auto;
`;

const CounterContainer = styled('div')`
  width: 100vw;
  position: fixed;
  top: ${MODAL_MAX_HEIGHT_PX + MODAL_TOP_MARGIN_PX}px;
  left: 0px;

  @media (max-height: 780px) {
    bottom: 0px;
    top: auto;
  }

  padding: ${tokens.spacer.x3};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${tokens.color.white};
  ${tokens.text('subtitle2')}
`;

const updateUrlParam = (searchParams: URLSearchParams) => {
  const newUrl = `${window.location.protocol}//${window.location.host}${
    window.location.pathname
  }?${searchParams.toString()}`;

  window.history.replaceState({ path: newUrl }, '', newUrl);
};

interface HighlightsModalProps {
  selectedHighlightId: string | null;
  currentUser: React.ComponentProps<typeof SingleHighlight>['currentUser'];
  currentHighlightIndex: number | null;
  highlightsCount: number | null;
  organizationId: string;
  siteId: string;
  onClose: () => void;
  showPrevious: () => void;
  showNext: () => void;
}

export const HighlightModal = ({
  selectedHighlightId,
  currentUser,
  currentHighlightIndex,
  highlightsCount,
  organizationId,
  siteId,
  onClose,
  showPrevious,
  showNext,
}: HighlightsModalProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const openModal = (highlightId: string) => {
    setIsModalOpen(true);

    const openSearchParams = new URLSearchParams(window.location.search);
    openSearchParams.set('highlight', highlightId);
    updateUrlParam(openSearchParams);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    onClose();

    const closeSearchParams = new URLSearchParams(window.location.search);
    closeSearchParams.delete('highlight');
    updateUrlParam(closeSearchParams);
  };

  const isNextButtonAvailable = (): boolean => {
    return (
      currentHighlightIndex !== null &&
      highlightsCount !== null &&
      currentHighlightIndex < highlightsCount - 1
    );
  };

  useEffect(() => {
    if (selectedHighlightId) {
      openModal(selectedHighlightId);
    }
  }, [selectedHighlightId]);

  return (
    <Wrapper>
      <StyledModal
        appElementSelector="body"
        isOpen={isModalOpen}
        onClose={closeModal}
        hideCloseButton
        contentLabel={t('highlights:heatmaps.modalContentLabel')}
      >
        <NavigationButtonContainer>
          {currentHighlightIndex ? (
            <NavigationIconButton
              onClick={showPrevious}
              icon="arrow_left"
              tooltipText={t('highlights:modal.previous')}
              kind="ghost"
              size="m"
              data-testid="previous-highlight-button"
            />
          ) : (
            <div />
          )}
          {isNextButtonAvailable() && (
            <NavigationIconButton
              onClick={showNext}
              icon="arrow_right"
              tooltipText={t('highlights:modal.next')}
              kind="ghost"
              size="m"
              data-testid="next-highlight-button"
            />
          )}
        </NavigationButtonContainer>
        {selectedHighlightId && (
          <SingleHighlight
            currentUser={currentUser}
            organizationId={organizationId}
            onClose={closeModal}
            highlightId={selectedHighlightId}
            siteId={siteId}
          />
        )}
        <CounterContainer>
          {(currentHighlightIndex ?? 0) + 1} / {highlightsCount}
        </CounterContainer>
      </StyledModal>
    </Wrapper>
  );
};
